import styled, { css } from 'styled-components';

import { thumbnailOverflowWrapper } from '@pkg/components/Thumbnail/tokens';
import {
    floorGameColor as color,
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from './WalletOption.tokens';

interface ThumbnailOuterProps {
    $connected?: boolean;
}

const optionBase = css`
    border-radius: ${tokens.option.radius}px;
    background-color: rgba(255, 255, 255, 0.04);
    box-shadow: 0 0 14px rgba(0, 0, 0, 0.1);
`;

export const WalletAction = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};
    ${optionBase};
    width: 100%;
    outline: 2px solid transparent !important;
    outline-offset: 2px;
    opacity: 1;
    transition-property: background-color, outline-color, opacity;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:disabled {
        ${styledInteraction.interactionDisable};
        opacity: 0.8;
    }

    &:focus-visible {
        outline-color: ${designColor.brandPurple} !important;
    }

    &:hover:not(:active) {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

export const ConnectedOption = styled.div`
    ${optionBase};
`;

export const Interior = styled.div`
    display: flex;
    justify-content: space-between;
    padding: ${tokens.option.padding}px;
`;

///
/// Primary Column

export const PrimaryColumn = styled.div`
    display: flex;
    align-content: center;
    align-items: center;
    gap: ${tokens.primary.gap}px;
`;

export const PrimaryDetails = styled.div`
    display: grid;
    justify-items: start;
    gap: ${tokens.primary.detailsGap}px;
    flex: 1 1 auto;
`;

export const ThumbnailOuter = styled.div<ThumbnailOuterProps>`
    position: relative;
    z-index: 1;
    flex: 0 0 auto;

    // TODO: Implement gradient on pseudo elements.
    /* background: linear-gradient(45deg, #5dba24, #edff18); */

    &::before,
    &::after {
        ${styledInteraction.interactionDisable};
        content: '';
        display: block;
        position: absolute;
        opacity: ${({ $connected }) => ($connected ? 1 : 0)};
        transition: opacity ${duration.normal}ms ${easing.cubic};
    }

    &::before {
        z-index: 2;
        inset: 0;
        border-radius: ${tokens.thumbnail.radius}px;
        box-shadow: 0 0 0 2px ${color.green};
    }

    &::after {
        z-index: 3;
        top: -2px;
        right: -2px;
        width: 8px;
        height: 8px;
        border-radius: 999px;
        background-color: ${color.green};
    }
`;

export const ThumbnailInner = styled.div`
    ${thumbnailOverflowWrapper(tokens.thumbnail.radius)};
    width: ${tokens.thumbnail.size}px;
    height: ${tokens.thumbnail.size}px;
`;

export const WalletName = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    font-size: ${tokens.primary.nameSize}px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

export const DisconnectAction = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};
    display: flex;
    color: ${color.green};
    border-radius: 2px;
    outline: 2px solid transparent !important;
    outline-offset: 2px;
    transition-property: color, outline-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:disabled {
        ${styledInteraction.interactionDisable};
    }

    &:focus-visible {
        outline-color: ${designColor.brandPurple} !important;
    }

    &:hover:not(:active) {
        color: ${color.forest};
    }
`;

export const DisconnectText = styled.span`
    ${styledTypography.allianceBase};
    ${styledTypography.textBoxTrim};
    display: block;
    font-size: ${tokens.primary.disconnectSize}px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: currentColor;
`;

///
/// Secondary Column

export const SecondaryColumn = styled.div`
    display: flex;
    align-content: center;
    align-items: center;
`;

export const SupportedCurrencyList = styled.ul`
    ${styledReset.list};
    display: grid;
    grid-auto-flow: column;
    align-content: center;
    gap: ${tokens.supportedCurrency.gap}px;
`;

export const SupportedCurrencyItem = styled.li`
    display: grid;
    place-items: center;
    place-content: center;
    width: ${tokens.supportedCurrency.itemSize}px;
    height: ${tokens.supportedCurrency.itemSize}px;
    border-radius: 999px;
    background-color: rgb(255, 255, 255, 0.1);
`;

export const CurrencyIconWrapper = styled.div`
    width: ${tokens.supportedCurrency.iconSize}px;
    height: ${tokens.supportedCurrency.iconSize}px;
    color: rgb(255, 255, 255, 0.6);
`;

export const BalanceList = styled.ul`
    ${styledReset.list};
    display: flex;
    flex-wrap: wrap;
    justify-content: end;
    align-content: center;
    gap: ${tokens.supportedCurrency.gap}px;
`;

export const BalanceItem = styled.li``;
